import { ServiceApi } from "./ServiceApiWithLoading";
import Task from '../model/Task';
import { PaginationOptions, PaginationResult, promiseGetBlob, promiseGetRequest, promiseRequest, promiseSendFormData } from './ServiceApi';
import CommentTaskType from "../enum/CommentTaskType";
import TaskPostServiceStatus from "../enum/TaskPostServiceStatus";
import AttachmentTaskType from "../enum/AttachmentTaskType";
import TaskSendEmailType from "../enum/TaskSendEmailType";
import CommentTask from "../model/CommentTask";
import TaskRecurrenceType from "../enum/TaskRecurrenceType";
import CommentTaskUser from "../model/CommentTaskUser";

export default class TaskApi extends ServiceApi<Task> {

    constructor() {
        super('task');
        this.updateProducts = this.updateProducts.bind(this);
        this.saveComment = this.saveComment.bind(this);
        this.deleteComment = this.deleteComment.bind(this);
        this.saveAttachment = this.saveAttachment.bind(this);
        this.updateAttachment = this.updateAttachment.bind(this);
        this.deleteAttachment = this.deleteAttachment.bind(this);
        this.updatePostService = this.updatePostService.bind(this);
        this.updateCustomerContacts = this.updateCustomerContacts.bind(this);
        this.updateObservers = this.updateObservers.bind(this);
        this.updateTags = this.updateTags.bind(this);
        this.cloneCommentAttachmentsToTask = this.cloneCommentAttachmentsToTask.bind(this);
        this.linkCommentToTask = this.linkCommentToTask.bind(this);
        this.updateReferences = this.updateReferences.bind(this);
        this.sendEmail = this.sendEmail.bind(this);        
    }

    async iaCloseSuggestions(description: string | null, resolution: string | null, comments: string | null): Promise<string[]> {
        var baseUrl = this.getBaseUrl();
        var result = await this.promiseWithLoading(promiseRequest(baseUrl + `iaCloseSuggestions/`, 'POST', { description, resolution, comments }, true));
        //esperado um array de strings, se não for um array de strings, retornar array vazio
        if (!Array.isArray(result) || !result.every(item => typeof item === 'string')) {
            return [];
        }
        return result;
    }

    async resumeTaskGemini(taskId: number | string) {
        var baseUrl = this.getBaseUrl();
        var result = await this.promiseWithLoading(promiseRequest(baseUrl + `${taskId}/resumeTaskGemini/`, 'GET', undefined, true));
        return result;
    }

    async sendCommentsEmail(list: number[]) {
        var baseUrl = this.getBaseUrl();
        var result = await this.promiseWithLoading(promiseRequest(baseUrl + `sendCommentsEmail/`, 'POST', list || undefined, true));
        return result;
    }

    async updateProducts(taskId: number | string, list: number[] | null) {
        var baseUrl = this.getBaseUrl();
        var result = await this.promiseWithLoading(promiseRequest(baseUrl + `${taskId}/products/`, 'PUT', list || undefined, true));
        return result;
    }

    async updateCustomerContacts(taskId: number | string, list: number[] | null) {
        var baseUrl = this.getBaseUrl();
        var result = await this.promiseWithLoading(promiseRequest(baseUrl + `${taskId}/customerContacts/`, 'PUT', list || undefined, true));
        return result;
    }

    async updateObservers(taskId: number | string, list: number[] | null) {
        var baseUrl = this.getBaseUrl();
        var result = await this.promiseWithLoading(promiseRequest(baseUrl + `${taskId}/observers/`, 'PUT', list || undefined, true));
        return result;
    }

    async updateTags(taskId: number | string, list: number[] | string[] | null) {
        var baseUrl = this.getBaseUrl();
        var result = await this.promiseWithLoading(promiseRequest(baseUrl + `${taskId}/tags/`, 'PUT', list || undefined, true));
        return result;
    }

    async updateReferences(taskId: number | string, list: number[] | string[] | null) {
        var baseUrl = this.getBaseUrl();
        var result = await this.promiseWithLoading(promiseRequest(baseUrl + `${taskId}/references/`, 'PUT', list || undefined, true));
        return result;
    }

    async linkCommentToTask(commentId: number | string, taskId: number | string) {
        var baseUrl = this.getBaseUrl();
        var result = await this.promiseWithLoading(promiseRequest(baseUrl + `${commentId}/linkCommentToTask/`, 'PUT', { task_id: taskId }, true));
        return result;
    }

    async cloneCommentAttachmentsToTask(commentId: number | string, taskId: number | string) {
        var baseUrl = this.getBaseUrl();
        var result = await this.promiseWithLoading(promiseRequest(baseUrl + `${commentId}/cloneCommentAttachmentsToTask/`, 'POST', { task_id: taskId }, true));
        return result;
    }

    async updatePostService(taskId: number | string, post_service_status: TaskPostServiceStatus, software_improvement_needed: number | null,
        hardware_improvement_needed: number | null, documentation_improvement_needed: number | null) {
        var baseUrl = this.getBaseUrl();
        var result = await this.promiseWithLoading(promiseRequest(baseUrl + `${taskId}/postService/`, 'PUT', {
            post_service_status, software_improvement_needed, hardware_improvement_needed, documentation_improvement_needed
        }, true));
        return result;
    }

    async updateRecurrence(taskId: number | string, recurrence_type: TaskRecurrenceType, recurrence_ticket_status_id: number | null,
        recurrence_max: number | null, recurrence_value: number | null, recurrence_message: string | null) {
        var baseUrl = this.getBaseUrl();
        var result = await this.promiseWithLoading(promiseRequest(baseUrl + `${taskId}/recurrence/`, 'PUT', {
            recurrence_type, recurrence_ticket_status_id, recurrence_max, recurrence_value, recurrence_message
        }, true));
        return result;
    }

    async saveComment(taskId: number | string, comment: string, is_public: boolean, user_id: number, type: CommentTaskType, hours?: number, 
        minutes?: number, assigned_users?: number[] | null, prod_amount?: number, prod_amount_problem?: number, send_comment_to_approver?: boolean, notify_others_users?: number[], notify_approver?: boolean): Promise<CommentTask> {
        var baseUrl = this.getBaseUrl();
        var result = await this.promiseWithLoading(promiseRequest(baseUrl + `${taskId}/comment/`, 'POST', { comment, user_id, is_public, type, hours, minutes, assigned_users, prod_amount, prod_amount_problem, send_comment_to_approver, notify_others_users, notify_approver }, true));
        return result;
    }

    async markViewedComments(taskId: number | string, user_id: number): Promise<CommentTask> {
        var baseUrl = this.getBaseUrl();
        var result = await this.promiseWithLoading(promiseRequest(baseUrl + `${taskId}/markViewedComments/`, 'PUT', { user_id }, true));
        return result;
    }

    async resolveComment(commentTaskUserId: number | string): Promise<CommentTaskUser> {
        var baseUrl = this.getBaseUrl();
        var result = await this.promiseWithLoading(promiseRequest(baseUrl + `${commentTaskUserId}/resolveComment/`, 'PUT', {}, true));
        return result;
    }

    async awaredComment(commentTaskUserId: number | string): Promise<CommentTaskUser> {
        var baseUrl = this.getBaseUrl();
        var result = await this.promiseWithLoading(promiseRequest(baseUrl + `${commentTaskUserId}/awaredComment/`, 'PUT', {}, true));
        return result;
    }

    async cancelComment(commentTaskId: number | string): Promise<CommentTask> {
        var baseUrl = this.getBaseUrl();
        var result = await this.promiseWithLoading(promiseRequest(baseUrl + `${commentTaskId}/cancelComment/`, 'PUT', {}, true));
        return result;
    }

    async deleteComment(commentTaskId: number | string) {
        var baseUrl = this.getBaseUrl();
        var result = await this.promiseWithLoading(promiseRequest(baseUrl + `${commentTaskId}/comment/`, 'DELETE', {}, true));
        return result;
    }

    async saveAttachment(data: File | null, taskId: number | string, description: string, type: number, comment_task_id?: number | null, system_attachment_id?: number | null): Promise<any> {
        var baseUrl = this.getBaseUrl();
        const formData = new FormData();

        formData.append('description', description);
        formData.append('type', String(type));
        if (comment_task_id) {
            formData.append('comment_task_id', String(comment_task_id));
        }
        if (system_attachment_id) {
            formData.append('system_attachment_id', String(system_attachment_id));
        }
        if (data) {
            formData.append('file', data);
        }

        try {
            var result = await this.promiseWithLoading(promiseSendFormData(baseUrl + `${taskId}/attachment/`, 'POST', formData));
            return result;
        } catch {
            return null;
        }
    }

    async deleteAttachment(attachmentTaskId: number | string) {
        var baseUrl = this.getBaseUrl();
        var result = await this.promiseWithLoading(promiseRequest(baseUrl + `${attachmentTaskId}/attachment/`, 'DELETE', {}, true));
        return result;
    }

    async updateAttachment(attachmentTaskId: number | string, description: string, type: AttachmentTaskType) {
        var baseUrl = this.getBaseUrl();
        var result = await this.promiseWithLoading(promiseRequest(baseUrl + `${attachmentTaskId}/attachment/`, 'PUT', { description: description, type: type }, true));
        return result;
    }

    async sendEmail(taskId: number | string, type: TaskSendEmailType, user_id_old?: number | null, ticket_status_id_old?: number | null) {
        var baseUrl = this.getBaseUrl();
        var result = await this.promiseWithLoading(promiseRequest(baseUrl + `${taskId}/sendEmail/`, 'POST', { type: type, user_id_old: user_id_old, ticket_status_id_old: ticket_status_id_old }, true));
        return result;
    }

    async sendEmailServiceEvaluation(taskId: number | string) {
        var baseUrl = this.getBaseUrl();
        var result = await this.promiseWithLoading(promiseRequest(baseUrl + `${taskId}/sendEmailServiceEvaluation/`, 'POST', {}, true));
        return result;
    }

    async sendEmailRequiresApproval(taskId: number | string) {
        var baseUrl = this.getBaseUrl();
        var result = await this.promiseWithLoading(promiseRequest(baseUrl + `${taskId}/sendEmailRequiresApproval/`, 'POST', {}, true));
        return result;
    }

    async getExportCsvList(options: PaginationOptions<Task>): Promise<Blob | null> {
        var url = this.getBaseUrl() + 'exportList';
        url = this.getUrl(url, options);
        return await this.promiseWithLoading(promiseGetBlob(url));
    }

    async getBlobPdf(taskId: number | string): Promise<Blob | null> {
        var baseUrl = this.getBaseUrl();
        let result = await this.promiseWithLoading(this.promiseGetBlob(baseUrl + `${taskId}/exportPdf`));
        return result;
    }

    async deleteChecklist(taskChecklistId: number | string) {
        var baseUrl = this.getBaseUrl();
        var result = await this.promiseWithLoading(promiseRequest(baseUrl + `${taskChecklistId}/deleteChecklist/`, 'DELETE', {}, true));
        return result;
    }

    async saveChecklist(taskId: number | string, checklist_id: number | string, is_mandatory: boolean) {
        var baseUrl = this.getBaseUrl();
        var result = await this.promiseWithLoading(promiseRequest(baseUrl + `${taskId}/saveChecklist/`, 'POST', { checklist_id, is_mandatory }, true));
        return result;
    }

    async checklistNotFilled(taskChecklistId: number | string, not_filled_user_id: number, not_filled: boolean) {
        var baseUrl = this.getBaseUrl();
        var result = await promiseRequest(baseUrl + `${taskChecklistId}/checklistNotFilled/`, 'PUT', { not_filled, not_filled_user_id }, true);
        return result;
    }

    async updateChecklistIsMandatory(taskChecklistId: number | string, is_mandatory: boolean, withLoad?: boolean): Promise<CommentTask> {
        var baseUrl = this.getBaseUrl();
        if (withLoad) {
            var result = await this.promiseWithLoading(promiseRequest(baseUrl + `${taskChecklistId}/updateChecklistIsMandatory/`, 'PUT', { is_mandatory }, true));
        } else {
            var result = await promiseRequest(baseUrl + `${taskChecklistId}/updateChecklistIsMandatory/`, 'PUT', { is_mandatory }, true);
        }
        return result;
    }

    async approveTask(taskId: number | string, justification: string, isAnalysis: boolean) {
        var baseUrl = this.getBaseUrl();
        return await this.promiseWithLoading(promiseRequest(baseUrl + `${taskId}/approve/`, 'POST', { justification, is_analysis: isAnalysis }, true));
    }

    async denyTask(taskId: number | string, justification: string, isAnalysis: boolean) {
        var baseUrl = this.getBaseUrl();
        return await this.promiseWithLoading(promiseRequest(baseUrl + `${taskId}/deny/`, 'POST', { justification, is_analysis: isAnalysis }, true));
    }

    async requestMoreInfoApi(taskId: number | string, comment: string) {
        var baseUrl = this.getBaseUrl();
        return await this.promiseWithLoading(promiseRequest(baseUrl + `${taskId}/requestMoreInfo/`, 'POST', { comment }, true));
    }

}

